/**
 * Unified debug logger for the app
 * 
 * Usage:
 * - Normal browsing: console is clean
 * - ?debug=1: Shows debug logs
 * - ?perf=1: Shows perf logs (handled separately by devPerfLog.ts)
 * 
 * Errors always show regardless of debug flag.
 */

// Check URL param once at load time for performance
const getDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch {
    return false;
  }
};

const DEBUG_ENABLED = getDebugEnabled();

/**
 * Debug log - only shows when ?debug=1
 */
export const log = (...args: unknown[]): void => {
  if (DEBUG_ENABLED) console.log(...args);
};

/**
 * Debug warn - only shows when ?debug=1
 */
export const warn = (...args: unknown[]): void => {
  if (DEBUG_ENABLED) console.warn(...args);
};

/**
 * Error log - ALWAYS shows (errors are important)
 */
export const error = (...args: unknown[]): void => {
  console.error(...args);
};

/**
 * Alias for error - ALWAYS shows
 */
export const logError = (...args: unknown[]): void => {
  console.error(...args);
};

/**
 * Grouped log - only shows when ?debug=1
 */
export const group = (label: string): void => {
  if (DEBUG_ENABLED) console.group(label);
};

export const groupEnd = (): void => {
  if (DEBUG_ENABLED) console.groupEnd();
};

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = (): boolean => DEBUG_ENABLED;
